<template>
    <Loading v-if="loading"/>
    <Page>
        <Container>
            <div class="mx-auto max-w-screen-md">

                <div class="flex items-center justify-between mb-6">
                    <Title>{{pageTitle}}</Title>
                    <Button secondary outlined size="sm" @click="handleSettingsFeedback">
                        Give Feedback
                    </Button>
                </div>

                <FormSections>

                    <FormSection title="Automate" description="Settings to streamline your workouts.">

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Last Band(s)</Label>
                                <Help>
                                    Automatically select the last band(s) used when an exercise is added.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.autoLastBands"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Auto Start Rest Timer</Label>
                                <Help>
                                    Automatically start a rest timer after saving the exercise directly above it.
                                </Help>
                                <Help class="text-red-500" v-if="form.fields.autoStartRestTimerOnExerciseSave && !form.fields.restTimerEnabled">
                                    This setting won't do anything, since Rest Timer is disabled.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.autoStartRestTimerOnExerciseSave"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Auto Open Next Exercise</Label>
                                <Help>
                                    Automatically open the next exercise when a rest timer completes.
                                </Help>
                                <Help class="text-red-500" v-if="form.fields.autoOpenNextExerciseOnRestTimerComplete && !form.fields.restTimerEnabled">
                                    This setting won't do anything, since Rest Timer is disabled.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.autoOpenNextExerciseOnRestTimerComplete"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Open Timer</Label>
                                <Help>
                                    Automatically open the timer when editing an exercise.
                                </Help>
                                <Help class="text-red-500" v-if="form.fields.autoOpenTimer && !form.fields.exerciseTimer">
                                    This setting won't do anything, since the Exercise Timer is disabled.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.autoOpenTimer"/>
                        </Field>

                    </FormSection>

                    <FormSection title="Miscellaneous" description="A few random settings to help.">

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Zero Rep Warning</Label>
                                <Help>
                                    This will warn you before letting you save a workout with zero reps.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.warnZeroReps"/>
                        </Field>

                    </FormSection>

                    <FormSection title="Workout Features"
                                 description="Settings to show/hide features on the Workout form.">

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Calendar</Label>
                                <Help>
                                    Show the Calendar view for reviewing workouts and logs by day.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.calendarEnabled"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Timer</Label>
                                <Help>
                                    Enable/Disable the timer related inputs.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.workoutTimer"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Rest Timer</Label>
                                <Help>
                                    Show configured rest timers when starting workouts from templates.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.restTimerEnabled"/>
                        </Field>

                        <Field v-if="form.fields.restTimerEnabled">
                            <Label>Default Rest Timer</Label>
                            <div class="flex items-center gap-3">
                                <NumberInput :min="1" :max="7200" v-model="form.fields.restTimerDuration"/>
                                <div class="text-sm text-neutral-600 dark:text-neutral-400">seconds</div>
                            </div>
                        </Field>

                        <Field v-if="form.fields.restTimerEnabled" class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Countdown Sound</Label>
                                <Help>
                                    Play countdown beeps as each rest timer finishes.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.restTimerCountdownSound"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Progress Photo</Label>
                                <Help>
                                    Enable/Disable the progress photo related inputs.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.workoutProgressPhoto"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Notes</Label>
                                <Help>
                                    Enable/Disable the notes related inputs.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.workoutNotes"/>
                        </Field>

                    </FormSection>

                    <FormSection title="Exercise Features"
                                 description="Settings to show/hide features on the Exercise form.">

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Timer</Label>
                                <Help>
                                    Enable/Disable the timer related inputs.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.exerciseTimer"/>
                        </Field>

                        <Field v-if="form.fields.exerciseTimer">
                            <Label>Metronome Pattern</Label>
                            <SelectInput v-model="form.fields.metronomePattern"
                                         :options="metronomePatternOptions"
                            />
                            <Help>
                                Choose how often the alternate metronome beep should play.
                            </Help>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Advanced Toggle</Label>
                                <Help>
                                    Enable/Disable the advanced toggle input.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.exerciseAdvancedToggle"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Exhaustion Level</Label>
                                <Help>
                                    Enable/Disable the exhaustion level input.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.exerciseExhaustionLevel"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Notes</Label>
                                <Help>
                                    Enable/Disable the notes related inputs.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.exerciseNotes"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Previous Reps</Label>
                                <Help>
                                    Enable/Disable the Previous Reps data.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.metaPrevReps"/>
                        </Field>

                        <Field class="flex items-center justify-between gap-4">
                            <div>
                                <Label>Max Reps</Label>
                                <Help>
                                    Enable/Disable the Max Reps data.
                                </Help>
                            </div>
                            <Switch v-model="form.fields.metaMaxReps"/>
                        </Field>

                    </FormSection>

                    <FormSection title="Meta" description="Settings related to meta data.">

                        <Field v-if="form.fields.metaMaxReps">
                            <Label>Max Reps Source</Label>
                            <SelectInput v-model="form.fields.metaMaxRepsSource"
                                         :options="metaMaxRepsSourceOptions"
                            />
                            <Help>
                                What should the Max Reps data point be tied to?
                            </Help>
                        </Field>

                    </FormSection>

                </FormSections>

                <Controls>

                    <template v-slot:left>

                    </template>

                    <template v-slot:right>
                        <div class="flex items-center justify-between gap-4">
                            <Button primary @click="handleSave">
                                Save
                            </Button>
                        </div>
                    </template>
                </Controls>

            </div>
        </Container>
    </Page>
</template>

<script>
import {defineComponent} from 'vue'
import {Form} from '@/internal.js';
import {mapActions} from 'vuex';
import NumberInput from '@/components/NumberInput.vue';
import {openFeatureFeedbackPrompt} from '@/utils/feedback.js';

export default defineComponent({
  name: 'Form',
  components: {
    NumberInput,
  },
  computed: {
    pageTitle() {
      return 'Settings';
    },
    user() {
      return this.$store.getters['auth/user'];
    },
  },
  created() {
    this.loadUser();
  },
  data() {
    return {
      metaMaxRepsSourceOptions: [
        {
          label: 'Max Reps for the selected Band(s)',
          value: 'selected',
        },
        {
          label: 'Max Reps for any Band(s)',
          value: 'any',
        },
      ],
      metronomePatternOptions: [
        {
          label: '1 Second',
          value: 1,
        },
        {
          label: '2 Second',
          value: 2,
        },
        {
          label: '3 Second',
          value: 3,
        },
        {
          label: '4 Second',
          value: 4,
        },
        {
          label: '5 Second',
          value: 5,
        },
      ],
      form: new Form({
        autoLastBands: false,
        calendarEnabled: true,
        autoOpenNextExerciseOnRestTimerComplete: false,
        autoOpenTimer: false,
        autoStartRestTimerOnExerciseSave: false,
        exerciseAdvancedToggle: true,
        exerciseExhaustionLevel: true,
        exerciseNotes: true,
        exerciseTimer: true,
        metronomePattern: 1,
        metaMaxReps: true,
        metaMaxRepsSource: 'selected',
        metaPrevReps: true,
        metaPrevRepsSource: 'selected',
        restTimerCountdownSound: true,
        restTimerDuration: 90,
        restTimerEnabled: true,
        warnZeroReps: true,
        workoutNotes: true,
        workoutProgressPhoto: true,
        workoutTimer: true,
      }),
      loading: false,
    };
  },
  methods: {
    ...mapActions({
      myShow: 'my/show',
      mySettings: 'my/settings',
    }),
    fillForm() {
      this.form.fields = {
        ...this.form.fields,
        ...this.user.settings,
      };
    },
    handleSave() {

      if (this.loading) {
        return false;
      }

      let action = this.mySettings;
      let payload = {
        ...this.form.fields,
      };

      this.loading = true;
      action(payload).then(() => {
        window.app.snackbar({
          type: 'success',
          message: 'Settings Saved',
        });
      }).then(this.myShow).catch((error) => {
        this.form.recordErrors(error);
        window.app.snackbarError(error);
      }).finally(() => {
        this.loading = false;
      });
    },
    handleSettingsFeedback() {
      openFeatureFeedbackPrompt({
        context: {
          screen: 'settings',
        },
        promptLocation: 'settings',
      });
    },
    loadUser() {
      this.loading = true;
      return this.myShow().then(this.fillForm)
        .catch((error) => {
          window.app.snackbarError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
})
</script>

<style scoped lang="scss">

</style>
